import { get } from 'lodash'
import type { DeeplinkProps } from 'core/components/Deeplink'
import type {
  EntriesByRole,
  PhysicalLocationDetails,
} from 'core/http/cases/EntriesHttp.types'
import {
  EKATA_ADDRESS_SEARCH_URL,
  EKATA_IDENTITY_REVIEW_URL,
  EKATA_PHONE_SEARCH_URL,
  EkataIdentityReviewVariables,
  openEkataAddressSearch,
  openEkataIdentityReview,
  openEkataPhoneSearch,
} from 'core/utils/ekataDeepLink'
import {
  ADDRESS_SEARCH_LABEL,
  EMAIL_SEARCH_LABEL,
  PHONE_NUM_SEARCH_LABEL,
} from './constants'

export const EkataIDCheck = (entriesByRole: EntriesByRole): DeeplinkProps => {
  const params: EkataIdentityReviewVariables = {}

  const billingName = entriesByRole.cardHolder?.[0]?.entityName
  if (billingName) {
    params.billing_name = billingName
  }

  const billingPhone = entriesByRole.accountPhone?.[0]?.entityName
  if (billingPhone) {
    params.billing_phone = billingPhone
  }

  const billingAddressDetails: PhysicalLocationDetails | undefined = get(
    entriesByRole,
    'billingAddress[0].details'
  )

  if (billingAddressDetails) {
    const { street, cityName, regionAlpha, countryIsoCode } =
      billingAddressDetails

    if (street) {
      params.billing_address_street_line_1 = street
    }
    if (cityName) {
      params.billing_address_city = cityName
    }
    if (regionAlpha) {
      params.billing_address_state_code = regionAlpha
    }
    if (countryIsoCode) {
      params.billing_address_country_code = countryIsoCode
    }
  }

  const shippingPhone = entriesByRole.confirmationPhone?.[0]?.entityName
  if (shippingPhone) {
    params.shipping_phone = shippingPhone
  }

  const shippingName = entriesByRole.deliveryRecipient?.[0]?.entityName
  if (shippingName) {
    params.shipping_name = shippingName
  }

  const deliveryAddressDetails: PhysicalLocationDetails | undefined = get(
    entriesByRole,
    'deliveryAddress[0].details'
  )

  if (deliveryAddressDetails) {
    const { street, cityName, regionAlpha, countryIsoCode } =
      deliveryAddressDetails

    if (street) {
      params.shipping_address_street_line_1 = street
    }
    if (cityName) {
      params.shipping_address_city = cityName
    }
    if (regionAlpha) {
      params.shipping_address_state_code = regionAlpha
    }
    if (countryIsoCode) {
      params.shipping_address_country_code = countryIsoCode
    }
  }

  const emailAddress: string | null | undefined = get(
    entriesByRole,
    'confirmationEmail[0].details.emailAddress'
  )
  if (emailAddress) {
    params.email_address = emailAddress
  }

  const ipAddress: string | null | undefined = get(
    entriesByRole,
    'purchaseIP[0].details.ipAddress'
  )
  if (ipAddress) {
    params.ip_address = ipAddress
  }

  return {
    iconType: 'ekata',
    label: 'Ekata Identity Check',
    url: EKATA_IDENTITY_REVIEW_URL,
    onClick: () => openEkataIdentityReview(params),
  }
}

export const EkataAddress = ({
  street,
  cityName,
  regionAlpha,
  countryIsoCode,
}: PhysicalLocationDetails): DeeplinkProps => ({
  iconType: 'ekata',
  label: ADDRESS_SEARCH_LABEL,
  url: EKATA_ADDRESS_SEARCH_URL,
  onClick: () =>
    openEkataAddressSearch({
      street: street || undefined,
      where: [cityName, regionAlpha].filter(Boolean).join(' ') || undefined,
      country_code: countryIsoCode || undefined,
    }),
})

export const EkataPhone = (
  phoneNum: string | null | undefined
): DeeplinkProps | null | undefined =>
  phoneNum
    ? {
        iconType: 'ekata',
        label: PHONE_NUM_SEARCH_LABEL,
        url: EKATA_PHONE_SEARCH_URL,
        onClick: () => openEkataPhoneSearch({ number: phoneNum }),
      }
    : null

export const EkataEmail = (email: string): DeeplinkProps => ({
  iconType: 'ekata',
  label: EMAIL_SEARCH_LABEL,
  url: EKATA_IDENTITY_REVIEW_URL,
  onClick: () => openEkataIdentityReview({ email_address: email }),
})
